<template>
  <!-- Near-fullscreen via BaseModal's public API only: inline height:90vh wins over the scoped
       rule, the flex column lets the body own the scroll. closeOnBackdrop=false is legacy's
       no-close-on-backdrop — an accidental dismissal loses the operator's place. -->
  <BaseModal :open="open"
             maxWidth="1100px"
             :closeOnBackdrop="false"
             :position="['center', 'center']"
             :modalStyle="['height:90vh', 'display:flex', 'flex-direction:column']"
             @close="$emit('close')">
    <div class="v2026-ecm" role="dialog" aria-modal="true" aria-label="ประวัติข้อความในแชท">

      <div class="v2026-ecm__head">
        <div class="v2026-ecm__switchwrap">
          <!-- .v2026-andor__switch recipe from InboxToolbarV2026. No b-form-checkbox. -->
          <label class="v2026-ecm__switch">
            <input id="v2026-ecm-admin" type="checkbox" class="cursor-pointer"
                   v-model="showAdminMessages" @change="onToggleAdmin">
            <span class="v2026-ecm__slider" aria-hidden="true"></span>
          </label>
          <label class="v2026-ecm__switchlabel" for="v2026-ecm-admin">แสดงข้อความแอดมิน</label>
        </div>
        <button type="button" class="v2026-ecm__close" aria-label="ปิด"
                @click="$emit('close')">×</button>
      </div>

      <div class="v2026-ecm__body" ref="scrollEl" tabindex="-1" @scroll.passive="onScroll">

        <!-- sticky + height:0 on purpose: the page-up anchor snapshots scrollHeight before the
             fetch, so a real-height indicator would shift the restore by its own size. -->
        <div class="v2026-ecm__stickytop">
          <div v-if="pagingUp" class="v2026-ecm__paging" role="status" aria-live="polite">
            <i class="fas fa-spinner fa-pulse"></i> กำลังโหลดข้อความก่อนหน้า…
          </div>
          <div v-else-if="pageError" class="v2026-ecm__paging v2026-ecm__paging--error" role="status">
            โหลดข้อความก่อนหน้าไม่สำเร็จ
            <button type="button" class="v2026-ecm__retry v2026-ecm__retry--inline"
                    @click="retry()">ลองใหม่</button>
          </div>
        </div>

        <div v-if="loading" class="v2026-ecm__state" role="status" aria-live="polite">
          <i class="fas fa-spinner fa-pulse"></i> กำลังโหลดข้อความ…
        </div>

        <div v-else-if="error" class="v2026-ecm__state" role="status" aria-live="polite">
          <span class="v2026-ecm__alert" aria-hidden="true">!</span>
          <span class="v2026-ecm__state-line">โหลดข้อความไม่สำเร็จ</span>
          <button type="button" class="v2026-ecm__retry" @click="retry()">
            <span class="v2026-ecm__retry-ico" aria-hidden="true" :style="maskStyle(icons.refresh)"></span>
            ลองใหม่
          </button>
        </div>

        <!-- own=0 makes an empty result recoverable, so it points back at the switch above. -->
        <div v-else-if="!rows.length" class="v2026-ecm__state" role="status" aria-live="polite">
          <template v-if="showAdminMessages">
            <span class="v2026-ecm__state-line">ไม่มีข้อความในแชทนี้</span>
          </template>
          <template v-else>
            <span class="v2026-ecm__state-line">ยังไม่มีข้อความจากลูกค้าในแชทนี้</span>
            <span class="v2026-ecm__state-hint">เปิด “แสดงข้อความแอดมิน” เพื่อดูข้อความทั้งหมด</span>
          </template>
        </div>

        <template v-else>
          <div v-if="reachedEnd" class="v2026-ecm__start">
            <span>จุดเริ่มต้นของการสนทนา</span>
          </div>
          <div v-for="(message, index) in rows" :key="message.id || 'ecm-' + index">
            <!-- Attachments render as a link row that emits no @load. MessageAttachment's
                 @load commits the GLOBAL COUNT_LOADED_IMAGE, which fires the main pane's
                 auto-scroll on a strict count match — a second live tree would satisfy it on
                 behalf of an image that has not loaded and mis-position the pane behind us.
                 Text messages still use the real CustomerChat / AdminChat. -->
            <template v-if="hasAttachment(message)">
              <div class="v2026-ecm__att" :class="message.own === 0 ? 'is-in' : 'is-out'">
                <div class="v2026-ecm__att-av" v-if="actorAvatar(message)">
                  <img :src="actorAvatar(message)" alt="" @error="onAvatarError">
                </div>
                <div class="v2026-ecm__att-main">
                  <div class="v2026-ecm__att-meta">
                    {{ actorName(message) }} | {{ message.created_at | chatDateTime }}
                  </div>
                  <div class="v2026-ecm__att-bubble">
                    <span class="v2026-ecm__att-ico" aria-hidden="true"
                          :style="maskStyle(icons.attach)"></span>
                    <span class="v2026-ecm__att-label">{{ attachmentLabel(message) }}</span>
                    <a v-if="attachmentUrl(message)" class="v2026-ecm__att-open"
                       :href="attachmentUrl(message)" target="_blank" rel="noopener noreferrer">เปิดดู</a>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <CustomerChat v-if="message.own === 0" :message="message" :selectedFile="[]" />
              <AdminChat v-else :message="message" :selectedFile="[]" />
            </template>
          </div>
        </template>

      </div>
    </div>
  </BaseModal>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import BaseModal from '@/components/coversation/BaseModal.vue'
import CustomerChat from '@/components/coversation/CustomerChat.vue'
import AdminChat from '@/components/coversation/AdminChat.vue'
import chatHelpers from '@/helpers/chatHelpers'

const PLACEHOLDER_AVATAR = 'https://beneat.co/static/img/unknown.png'

// Attachment type labels for the Plan-B row. Anything unlisted falls back to the generic label.
const ATTACHMENT_LABELS = {
  jpg: 'รูปภาพ',
  jpeg: 'รูปภาพ',
  png: 'รูปภาพ',
  gif: 'รูปภาพ',
  mp4: 'วิดีโอ',
  mp3: 'ไฟล์เสียง'
}
const ATTACHMENT_LABEL_DEFAULT = 'ไฟล์แนบ'

export default {
  name: 'ExpandChatModalV2026',
  components: {
    BaseModal,
    CustomerChat,
    AdminChat
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      // Component-LOCAL, the fetchIncognitoMessages pattern. Never read or write message/messages:
      // legacy blanked and re-filtered the live pane on open and repaired it on close.
      rows: [],
      page: 0,
      loading: false,
      pagingUp: false,
      showAdminMessages: false,
      error: false,
      pageError: false,
      reachedEnd: false,
      // Anti-jump anchor, snapshotted at scrollTop === 0. Same maths as Conversation.vue:826-832.
      contentHeight: 0,
      // Bumped per request; a stale response is dropped, so rapid toggling cannot let a slow
      // first answer overwrite a newer one.
      reqToken: 0,
      icons: {
        refresh: require('@/assets/icons2026/refresh.svg'),
        attach: require('@/assets/icons2026/composer-attach.svg')
      }
    }
  },
  computed: {
    ...mapState('aiAssistant', ['fcRequest'])
  },
  watch: {
    open (isOpen) {
      if (isOpen) {
        this.onOpen()
      } else {
        this.resetState()
      }
    },
    // Closes rather than re-fetching: a silently re-pointed history viewer is the worst state
    // for an operator reading a customer's words.
    conversationId () {
      if (this.open) this.$emit('close')
    },
    // The AI pill row is hidden by CSS in this modal; a safety net in case that hide ever breaks.
    fcRequest () {
      if (this.open && this.fcRequest) this.$emit('close')
    }
  },
  methods: {
    async onOpen () {
      this.showAdminMessages = false
      this.page = 0
      this.rows = []
      this.error = false
      this.pageError = false
      this.reachedEnd = false
      this.contentHeight = 0
      // No conversation ⇒ never fetch, so the modal stays inert on a branch without one.
      if (!this.conversationId) {
        this.reqToken = this.reqToken + 1
        this.loading = false
        return
      }
      await this.$nextTick()
      await this.fetchPage()
      this.focusBody()
    },
    resetState () {
      // Frees memory and stops a stale conversation flashing on reopen.
      this.reqToken = this.reqToken + 1
      this.rows = []
      this.page = 0
      this.loading = false
      this.pagingUp = false
      this.error = false
      this.pageError = false
      this.reachedEnd = false
      this.contentHeight = 0
    },
    async fetchPage () {
      if (!this.conversationId) return
      const token = this.reqToken + 1
      this.reqToken = token
      const isFirstPage = this.page === 0
      if (isFirstPage) {
        this.loading = true
        this.error = false
      } else {
        this.pagingUp = true
        this.pageError = false
      }
      const params = {
        conversation_id: this.conversationId,
        page: this.page,
        // MUST stay literal 0 / undefined: axios serialises 0 to "0", which is truthy, and
        // messageController.js:31 tests truthiness. Another serialiser kills the filter silently.
        own: this.showAdminMessages ? undefined : 0
      }
      try {
        const res = await axios.get('/messages', { params })
        if (token !== this.reqToken) return
        const messages = (res && res.status === 200 && Array.isArray(res.data)) ? res.data : []
        if (isFirstPage) {
          this.rows = messages
          this.reachedEnd = messages.length === 0
          await this.$nextTick()
          this.scrollToBottom()
        } else if (messages.length > 0) {
          // Older page goes IN FRONT — the incognito order. Reverse it and the list reverses.
          this.rows = [...messages, ...this.rows]
          await this.$nextTick()
          this.restoreScrollAnchor()
        } else {
          // Stops the infinite re-request legacy performs forever at the top.
          this.reachedEnd = true
        }
      } catch (e) {
        if (token !== this.reqToken) return
        // Inline state, never $swal. A failed page-up keeps the rows already on screen.
        if (isFirstPage) {
          this.error = true
          this.rows = []
        } else {
          this.pageError = true
        }
      } finally {
        if (token === this.reqToken) {
          this.loading = false
          this.pagingUp = false
        }
      }
    },
    onScroll (e) {
      const el = e && e.target
      if (!el || el.scrollTop !== 0) return
      if (this.loading || this.pagingUp || this.error || this.pageError) return
      if (this.reachedEnd || !this.conversationId) return
      this.contentHeight = el.scrollHeight
      this.page = this.page + 1
      this.fetchPage()
    },
    // Refetches the CURRENT page with the CURRENT switch state.
    retry () {
      this.error = false
      this.pageError = false
      this.fetchPage()
    },
    onToggleAdmin () {
      this.page = 0
      this.rows = []
      this.error = false
      this.pageError = false
      this.reachedEnd = false
      this.contentHeight = 0
      this.fetchPage()
    },
    // The modal owns its own scroll element and maths: never MainPanelV2026's helpers, never the
    // app-wide image counter.
    scrollToBottom () {
      const el = this.$refs.scrollEl
      if (el) el.scrollTop = el.scrollHeight
    },
    restoreScrollAnchor () {
      const el = this.$refs.scrollEl
      if (el) el.scrollTop = el.scrollHeight - this.contentHeight
    },
    focusBody () {
      const el = this.$refs.scrollEl
      // preventScroll: focusing must not undo the scroll position we just set.
      if (el && el.focus) el.focus({ preventScroll: true })
    },
    hasAttachment (message) {
      return !!(message && message.attachments && message.attachments.length > 0)
    },
    attachmentUrl (message) {
      return this.hasAttachment(message) ? message.attachments[0].url : null
    },
    attachmentLabel (message) {
      if (!this.hasAttachment(message)) return ATTACHMENT_LABEL_DEFAULT
      const ext = chatHelpers.getUrlExtension(message.attachments[0].url)
      return ATTACHMENT_LABELS[ext] || ATTACHMENT_LABEL_DEFAULT
    },
    actor (message) {
      if (!message) return null
      return message.own === 0 ? message.customer : message.admin
    },
    actorName (message) {
      const a = this.actor(message)
      return (a && a.name) ? a.name : '-'
    },
    actorAvatar (message) {
      const a = this.actor(message)
      return (a && a.profile_picture) ? a.profile_picture : PLACEHOLDER_AVATAR
    },
    onAvatarError (e) {
      if (e && e.target && e.target.src !== PLACEHOLDER_AVATAR) {
        e.target.src = PLACEHOLDER_AVATAR
      }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-ecm {
  --v2026-ecm-main:      #31C1D7;
  --v2026-ecm-main-soft: #31C1D714;
  --v2026-ecm-surface:   #FFFFFF;
  --v2026-ecm-hover:     #E9ECF1;
  --v2026-ecm-pill:      #EBEDF0;
  --v2026-ecm-border:    #E4E7EC;
  --v2026-ecm-hairline:  #EAECF0;
  --v2026-ecm-text:      #101828;
  --v2026-ecm-secondary: #667085;
  --v2026-ecm-muted:     #98A2B3;
  --v2026-ecm-danger:    #F04438;
  --v2026-ecm-r-bubble:  16px;

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.v2026-ecm__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 0 0 auto;
  padding: 16px 24px;
  background: var(--v2026-ecm-surface);
  border-bottom: 1px solid var(--v2026-ecm-pill);
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}
.v2026-ecm__switchwrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.v2026-ecm__switchlabel {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  cursor: pointer;
}
.v2026-ecm__close {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-ecm__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-ecm-main-soft);
  border-radius: 4px;
}

/* .v2026-andor__switch recipe, copied not imported. */
.v2026-ecm__switch {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
}
.v2026-ecm__switch input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}
.v2026-ecm__slider {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 999px;
  transition: background-color 0.15s ease;
}
.v2026-ecm__slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: transform 0.15s ease;
}
.v2026-ecm__switch:hover .v2026-ecm__slider {
  background: #bcc2cc;
}
.v2026-ecm__switch input:checked ~ .v2026-ecm__slider {
  background: var(--v2026-ecm-main);
}
.v2026-ecm__switch:hover input:checked ~ .v2026-ecm__slider {
  background: var(--v2026-ecm-main);
}
.v2026-ecm__switch input:checked ~ .v2026-ecm__slider::before {
  transform: translateX(16px);
}
.v2026-ecm__switch input:focus-visible ~ .v2026-ecm__slider {
  box-shadow: 0 0 0 3px var(--v2026-ecm-main-soft);
}

.v2026-ecm__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  background: var(--chat-bg, #F8F9FA);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  outline: none;
}

.v2026-ecm__stickytop {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 0;
  overflow: visible;
}
/* Absolute inside the zero-height sticky box, so it paints over the first rows instead of
   displacing them. Offsets stay 0: sticky resolves top against the scrollport. */
.v2026-ecm__paging {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: var(--v2026-ecm-muted);
  background: var(--chat-bg, #F8F9FA);
  border-bottom: 1px solid var(--v2026-ecm-hairline);
}
.v2026-ecm__paging--error {
  color: var(--v2026-ecm-danger);
}

.v2026-ecm__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
  padding: 40px 16px;
  font-size: 15px;
  color: var(--v2026-ecm-muted);
  text-align: center;
}
.v2026-ecm__state-line {
  font-size: 15px;
}
.v2026-ecm__state-hint {
  font-size: 13px;
}
.v2026-ecm__alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--v2026-ecm-danger);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
}

/* .v2026-btn--neutral, the same recipe the toolbar trigger uses. */
.v2026-ecm__retry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid var(--v2026-ecm-pill);
  border-radius: 4px;
  background: var(--v2026-ecm-surface);
  color: var(--v2026-ecm-secondary);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}
.v2026-ecm__retry:hover {
  background: var(--v2026-ecm-hover);
  color: var(--v2026-ecm-text);
}
.v2026-ecm__retry:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-ecm__retry--inline {
  height: 24px;
  padding: 0 8px;
}
.v2026-ecm__retry-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-ecm__start {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  font-size: 12px;
  color: var(--v2026-ecm-muted);
}
.v2026-ecm__start::before,
.v2026-ecm__start::after {
  content: '';
  flex: 1 1 auto;
  height: 1px;
  background: var(--v2026-ecm-hairline);
}

/* Attachment row: the bubble anatomy without an <img>, so it emits no @load and cannot reach
   message/COUNT_LOADED_IMAGE. */
.v2026-ecm__att {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
}
.v2026-ecm__att.is-out {
  flex-direction: row-reverse;
}
.v2026-ecm__att-av {
  flex: 0 0 auto;
}
.v2026-ecm__att-av img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.v2026-ecm__att-main {
  min-width: 0;
  max-width: 75%;
}
.v2026-ecm__att.is-out .v2026-ecm__att-main {
  text-align: right;
}
.v2026-ecm__att-meta {
  padding-bottom: 4px;
  font-size: 13px;
  color: var(--v2026-ecm-muted);
}
.v2026-ecm__att-bubble {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--v2026-ecm-border);
  border-radius: 14px;
  background: var(--v2026-ecm-surface);
  font-size: 15px;
  color: var(--v2026-ecm-text);
}
.v2026-ecm__att.is-out .v2026-ecm__att-bubble {
  border: 0;
  border-radius: var(--v2026-ecm-r-bubble);
  background: #dcedc8;
}
.v2026-ecm__att-ico {
  display: inline-block;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-ecm-secondary);
}
.v2026-ecm__att-label {
  white-space: nowrap;
}
.v2026-ecm__att-open {
  color: #007bff;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
}
.v2026-ecm__att-open:hover {
  text-decoration: underline;
}

/* Read-only surface: the AI action buttons stay in the live pane behind this dialog. Coupled to
   CustomerChat.vue:59-70 — revisit if that markup changes. */
::v-deep #message-customer .mt-3.d-flex {
  display: none;
}

/* Outside MainPanelV2026's scope, so without this the legacy bubbles fall back to the raw
   bootstrap skin. Mirrors MainPanelV2026.vue:255-298 one density step tighter. */
::v-deep .customer-chat {
  background: var(--v2026-ecm-surface) !important;
  opacity: 1 !important;
  box-shadow: none !important;
  border: 1px solid var(--v2026-ecm-border) !important;
  border-radius: 14px !important;
}
::v-deep .admin-chat {
  background: #dcedc8 !important;
  border-radius: var(--v2026-ecm-r-bubble) !important;
  box-shadow: none !important;
  border: 0 !important;
}
::v-deep .customer-chat .card-body,
::v-deep .admin-chat .card-body {
  color: var(--v2026-ecm-text);
  font-size: 15px;
}
::v-deep .text-chat-time {
  color: var(--v2026-ecm-muted);
  font-size: 13px;
}
::v-deep .image-cropper-md .img-circle,
::v-deep .d-flex.justify-content-end .img-circle {
  width: 32px !important;
  height: 32px !important;
  object-fit: cover;
}
::v-deep .d-flex.justify-content-end .text-smaller.text-muted.pt-2 {
  width: 100%;
  text-align: center;
  color: var(--v2026-ecm-muted);
  font-size: 12px;
  letter-spacing: 0.2px;
}
/* pb-4 (24px) on every legacy row is one step looser than this reader wants. */
::v-deep #message-customer > div,
::v-deep #message-admin > div {
  padding-bottom: 12px !important;
}

@media (max-width: 767px) {
  .v2026-ecm__body {
    padding: 12px;
  }
  .v2026-ecm__head {
    padding: 12px 16px;
  }
}
</style>
